// The eligible native editorial footer owns this opt-in. Main PDP/cart Affirm
// remains independent; provider wording, logo and eligibility are never rebuilt.
const APPROVED_HOSTS = new Set(['www.raffi-jewellers.ca', 'raffi-jewellers.ca', 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com']);
const PROTECTED = /rolex|tag(?:[\s_+\-]|%20)*heuer|inquiry-only/i;
let sequence = 0;
let cleanup: (() => void) | null = null;

export function stickyAffirmEnabled(product: HTMLElement, footer: HTMLElement, location: Pick<Location, 'hostname' | 'pathname' | 'search'> = window.location): boolean {
  return APPROVED_HOSTS.has(location.hostname)
    && /^\/(?:fr\/)?products\/[a-z0-9_-]+\/?$/i.test(location.pathname)
    && !/[?&](?:key|inquiry)=/i.test(location.search)
    && !PROTECTED.test([location.pathname, product.dataset.brand || '', product.dataset.product || ''].join(' '))
    && !!product.closest('main[data-title-signature-product]')
    && footer.hasAttribute('data-editorial-sticky') && footer.hasAttribute('data-maison-ink-sticky')
    && footer.closest('.js-product') === product;
}

export function stickyAffirmAmount(raw: string | undefined): number | null {
  if (typeof raw !== 'string' || !/^\d+(?:\.\d+)?$/.test(raw)) return null;
  const cents = Math.round(Number(raw) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

export function initStickyAffirm(): void {
  cleanup?.(); cleanup = null;
  const product = document.querySelector<HTMLElement>('main[data-title-signature-product] .js-product');
  const footer = product?.querySelector<HTMLElement>('[data-editorial-sticky]');
  if (!product || !footer || !stickyAffirmEnabled(product, footer)) return;
  const holder = footer.querySelector<HTMLElement>('[data-sticky-affirm]');
  const availability = footer.querySelector<HTMLElement>('[data-sticky-availability]');
  let active = true;
  let ready = false;
  let scheduled = false;
  let quoteKey = '';

  const clear = () => {
    if (!holder) return;
    if (holder.childNodes.length) holder.replaceChildren();
    holder.hidden = true;
  };
  const sync = () => {
    scheduled = false;
    if (!active || !footer.isConnected || !stickyAffirmEnabled(product, footer)) { clear(); return; }
    const pending = footer.dataset.optionPending === 'true' || footer.dataset.stockLoading === 'true' || product.dataset.purchasePending === 'true';
    const online = footer.dataset.editorialPurchasable === 'true' && product.dataset.priceHidden !== 'true';
    const selectedStock = product.dataset.stockVariant;
    // Match the genuine promo already shown on the main priced PDP. This
    // server-confirmed store state keeps its appointment route and a separate
    // question about in-store financing; it does not promise in-store terms.
    const instore = footer.hasAttribute('data-paired-concierge-sticky')
      && footer.dataset.editorialInstore === 'true' && product.dataset.priceHidden !== 'true'
      && (!selectedStock || product.dataset.purchaseAvailable === 'true');
    const unavailable = !online || (!pending && product.dataset.purchaseAvailable === 'false');
    if (availability) {
      availability.hidden = !unavailable;
      if (unavailable && availability.textContent !== (footer.dataset.labelAvailability || '')) availability.textContent = footer.dataset.labelAvailability || '';
    }
    const price = product.querySelector<HTMLElement>('.single-product__price.js-product-price');
    const amount = stickyAffirmAmount(price?.dataset.price);
    const variant = footer.querySelector<HTMLElement>('[data-editorial-purchase]')?.dataset.id
      || (instore ? footer.dataset.editorialVariant : '') || '';
    const eligible = holder && (instore || (online && !unavailable)) && !pending && amount !== null && variant
      && (!selectedStock || selectedStock === variant);
    if (!eligible) { quoteKey = ''; clear(); return; }
    const sku = product.querySelector<HTMLElement>('.single-product__sku.js-sp-sku')?.dataset.internalSku || undefined;
    const category = product.dataset.productType || undefined;
    const reference = footer.querySelector<HTMLElement>('[data-editorial-reference]')?.textContent || '';
    const nextKey = JSON.stringify([product.dataset.product, variant, amount, sku, category, reference]);
    if (quoteKey === nextKey) {
      // Provider rendering is asynchronous; no empty finance line while it loads.
      holder.hidden = !holder.textContent?.trim();
      return;
    }
    clear();
    quoteKey = '';
    if (!ready || !window.affirm) return;
    const mount = document.createElement('div');
    mount.id = `rf-sticky-affirm-${++sequence}`;
    mount.className = 'editorial-sticky__affirm-promo';
    holder.appendChild(mount);
    quoteKey = nextKey;
    try {
      // A fresh, unique node prevents a delayed quote for the previous selection
      // from painting the current product. Do not reuse or clone provider markup.
      const promo = window.affirm.ui.components.create('promo', {
        amount: amount!, pageType: 'product', affirmLogoType: 'logo', affirmLogoColor: 'black',
        sku, category, learnMoreShow: true,
      });
      promo.render(`#${mount.id}`);
      holder.hidden = !holder.textContent?.trim();
    } catch {
      clear(); // Keep the key to avoid repeated retries from unrelated DOM changes.
    }
  };
  const schedule = () => {
    if (!scheduled && active) { scheduled = true; queueMicrotask(sync); }
  };
  const observer = new MutationObserver(schedule);
  observer.observe(product, {
    subtree: true, childList: true, characterData: true, attributes: true,
    attributeFilter: ['data-price', 'data-id', 'data-product', 'data-brand', 'data-product-type', 'data-internal-sku',
      'data-price-hidden', 'data-purchase-available', 'data-purchase-pending', 'data-stock-variant',
      'data-editorial-purchasable', 'data-editorial-instore', 'data-editorial-variant', 'data-option-pending', 'data-stock-loading'],
  });
  cleanup = () => { active = false; observer.disconnect(); clear(); };
  sync();
  window.affirm?.ui.ready(() => { if (active) { ready = true; sync(); } });
}

document.addEventListener('DOMContentLoaded', initStickyAffirm);
document.addEventListener('product:swap', initStickyAffirm);
